import * as React from 'react';
import {
  applyServerErrors,
  BottomSheet,
  BottomSheetContent,
  Button,
  Form,
  FormField,
  Input,
  ReadOnlyField,
  useZodForm,
} from '@relay-towing/ui';
import { useUpdateProfile } from '../../hooks/useUpdateProfile';
import { profileSchema, type ProfileValues } from '../../lib/profileSchema';

/**
 * Settings' Profile: the member's name to change, their email shown. The
 * name is theirs in every company; the email is how they sign in, so it is
 * read here and changed nowhere in the app.
 */
export function ProfileSheet({
  open,
  onOpenChange,
  name,
  email,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  name: string;
  email?: string;
}) {
  const formId = React.useId();
  const update = useUpdateProfile();
  return (
    <BottomSheet open={open} onOpenChange={onOpenChange}>
      <BottomSheetContent
        title="Profile"
        footer={
          <>
            <Button
              variant="secondary"
              size="xl"
              pill
              disabled={update.isPending}
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form={formId}
              size="xl"
              pill
              loading={update.isPending}
            >
              Save
            </Button>
          </>
        }
      >
        <ProfileForm
          id={formId}
          name={name}
          email={email}
          onSave={async (values) => {
            await update.mutateAsync(values);
            onOpenChange(false);
          }}
        />
      </BottomSheetContent>
    </BottomSheet>
  );
}

/**
 * The fields, made each time the sheet opens from the name as it is then:
 * the sheet's content is unmounted while it is shut.
 */
function ProfileForm({
  id,
  name,
  email,
  onSave,
}: {
  id: string;
  name: string;
  email?: string;
  onSave: (values: ProfileValues) => Promise<void>;
}) {
  const form = useZodForm(profileSchema, { defaultValues: { name } });
  const save = async (values: ProfileValues) => {
    try {
      await onSave(values);
    } catch (error) {
      applyServerErrors(form, error, 'Your name could not be saved.');
    }
  };
  return (
    <Form form={form} id={id} gap={4} onSubmit={save}>
      <FormField form={form} name="name" label="Name">
        {(field) => <Input {...field} autoComplete="name" />}
      </FormField>
      {email ? (
        <ReadOnlyField
          label="Email"
          value={email}
          description="How you sign in, so it is not changed here."
        />
      ) : null}
    </Form>
  );
}
